// Topup Page - multi-currency (USD/KGS bank details, MDL via MIA, EUR/KZT/RUB via request + captcha for RUB)
import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import axios from "axios";
import { ArrowLeft, Building2, RefreshCw, CheckCircle2, QrCode } from "lucide-react";
import { SBER, API, pageVariants, tapAnimation } from "../utils/constants";
import { SberCard } from "../components/UIComponents";
import { CaptchaBox } from "../components/CaptchaBox";
import { TOPUP_CURRENCIES, TOPUP_CONFIG, METHOD_LABELS, BANK_DETAILS } from "../utils/topupConfig";

const SberHeader = ({ title, onBack }) => (
  <div className="px-5 pt-5 pb-4 flex items-center gap-3" style={{ background: SBER.white }}>
    <motion.button
      whileTap={tapAnimation}
      onClick={onBack}
      className="w-10 h-10 rounded-full flex items-center justify-center"
      style={{ background: SBER.grayPale }}
      data-testid="topup-back-btn"
    >
      <ArrowLeft className="w-5 h-5" style={{ color: SBER.gray }} />
    </motion.button>
    <h1 className="text-[18px] font-semibold" style={{ color: SBER.dark }}>{title}</h1>
  </div>
);

const Row = ({ label, value, mono }) => (
  <div className="flex justify-between py-2 border-b border-gray-100 gap-3">
    <span style={{ color: SBER.gray }}>{label}</span>
    <span className={`font-medium text-right ${mono ? 'font-mono' : ''}`} style={{ color: SBER.dark }}>{value}</span>
  </div>
);

const BankDetailsView = ({ amountNum, netAmount, currency, bank, onBack, onDone, t }) => (
  <motion.div className="min-h-screen pb-20" style={{ background: SBER.bg }} initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
    <SberHeader title={t ? t('topup.paymentDetails') : 'Реквизиты для перевода'} onBack={onBack} />
    <div className="px-5 py-4">
      <SberCard className="p-5 mb-4">
        <div className="text-center mb-4">
          <div className="w-16 h-16 rounded-full mx-auto mb-3 flex items-center justify-center" style={{ background: SBER.greenPale }}>
            <Building2 className="w-8 h-8" style={{ color: SBER.green }} />
          </div>
          <p className="font-bold text-[20px]" style={{ color: SBER.dark }} data-testid="topup-details-amount">{amountNum.toLocaleString()} {currency}</p>
          <p className="text-sm" style={{ color: SBER.gray }}>{t ? t('topup.toReceive') : 'К зачислению'}: {netAmount.toFixed(2)} {currency}</p>
        </div>
        <div className="space-y-1 text-sm">
          <Row label={t ? t('topup.bank') : 'Банк'} value={bank.bank} />
          <Row label={t ? t('topup.account') : 'Р/счёт'} value={bank.account} mono />
          <Row label={t ? t('topup.bik') : 'БИК'} value={bank.bik} />
          <Row label={t ? t('topup.recipient') : 'Получатель'} value={bank.recipient} />
          <Row label={t ? t('topup.inn') : 'ИНН'} value={bank.inn} />
        </div>
      </SberCard>
      <div className="p-4 rounded-xl mb-4" style={{ background: '#FEF3C7' }}>
        <p className="text-sm" style={{ color: '#92400E' }}>
          <strong>Важно:</strong> в назначении платежа укажите ваш номер телефона. Зачисление в течение 1–24 часов.
        </p>
      </div>
      <motion.button
        whileTap={{ scale: 0.98 }}
        onClick={() => {
          navigator.clipboard.writeText(`Банк: ${bank.bank}\nСчёт: ${bank.account}\nБИК: ${bank.bik}\nПолучатель: ${bank.recipient}\nИНН: ${bank.inn}`);
          toast.success(t ? t('topup.detailsCopied') : 'Реквизиты скопированы');
        }}
        className="w-full py-4 rounded-xl text-white font-semibold mb-3"
        style={{ background: SBER.green }}
        data-testid="copy-details-btn"
      >
        {t ? t('topup.copyDetails') : 'Скопировать реквизиты'}
      </motion.button>
      <motion.button whileTap={{ scale: 0.98 }} onClick={onDone} className="w-full py-4 rounded-xl font-semibold" style={{ background: SBER.grayPale, color: SBER.dark }} data-testid="topup-done-btn">
        {t ? t('actions.close') : 'Готово'}
      </motion.button>
    </div>
  </motion.div>
);

const RequestSuccessView = ({ requestId, amountNum, currency, method, onDone }) => (
  <motion.div className="min-h-screen pb-20" style={{ background: SBER.bg }} initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
    <SberHeader title="Заявка принята" onBack={onDone} />
    <div className="px-5 py-4">
      <SberCard className="p-6 text-center mb-4">
        <div className="w-16 h-16 rounded-full mx-auto mb-3 flex items-center justify-center" style={{ background: SBER.greenPale }}>
          <CheckCircle2 className="w-8 h-8" style={{ color: SBER.green }} />
        </div>
        <p className="font-bold text-[20px]" style={{ color: SBER.dark }}>{amountNum.toLocaleString()} {currency}</p>
        <p className="text-sm mt-1" style={{ color: SBER.gray }}>{METHOD_LABELS[method]?.ru || method}</p>
        <p className="text-[13px] mt-3 font-mono" style={{ color: SBER.green }} data-testid="topup-request-id">{requestId}</p>
      </SberCard>
      <div className="p-4 rounded-xl mb-4" style={{ background: SBER.greenPale }}>
        <p className="text-sm" style={{ color: SBER.dark }}>
          Реквизиты для оплаты придут в <strong>Уведомления</strong> и от службы поддержки в течение 15 минут. После оплаты средства зачисляются в течение 1–24 часов.
        </p>
      </div>
      <motion.button whileTap={{ scale: 0.98 }} onClick={onDone} className="w-full py-4 rounded-xl text-white font-semibold" style={{ background: SBER.green }} data-testid="topup-request-done-btn">
        Готово
      </motion.button>
    </div>
  </motion.div>
);

const TopupPage = ({ rate, onBack, onSuccess, onNavigate, t }) => {
  const [amount, setAmount] = useState('');
  const [currency, setCurrency] = useState('USD');
  const [method, setMethod] = useState(null);
  const [captchaToken, setCaptchaToken] = useState(null);
  const [loading, setLoading] = useState(false);
  const [view, setView] = useState('form');
  const [requestId, setRequestId] = useState(null);

  const cfg = TOPUP_CONFIG[currency];
  const amountNum = parseFloat(amount) || 0;
  const fee = amountNum * (cfg.fee / 100);
  const netAmount = amountNum - fee;
  const needsCaptcha = Boolean(cfg.captcha);
  const needsMethod = cfg.mode === 'request';
  const canSubmit = amountNum >= cfg.min && (!needsMethod || method) && (!needsCaptcha || captchaToken);

  const selectCurrency = (curr) => {
    setCurrency(curr);
    setAmount('');
    setMethod(TOPUP_CONFIG[curr].methods?.length === 1 ? TOPUP_CONFIG[curr].methods[0] : null);
    setCaptchaToken(null);
  };

  const submitRequest = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('wallet_token');
      const res = await axios.post(`${API}/wallet/topup-request`,
        { currency, amount: amountNum, method, captcha_token: captchaToken },
        { headers: { Authorization: `Bearer ${token}` } });
      setRequestId(res.data.request_id);
      setView('request-success');
      if (onSuccess) onSuccess();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Не удалось создать заявку');
      if (needsCaptcha) setCaptchaToken(null);
    } finally {
      setLoading(false);
    }
  };

  const handleTopup = () => {
    if (amountNum < cfg.min) {
      toast.error(`${t ? t('topup.min') : 'Минимум'} ${cfg.min} ${currency}`);
      return;
    }
    if (cfg.mode === 'mia') return onNavigate ? onNavigate('mia') : null;
    if (cfg.mode === 'bank') return setView('details');
    submitRequest();
  };

  if (view === 'details') {
    return <BankDetailsView amountNum={amountNum} netAmount={netAmount} currency={currency} bank={BANK_DETAILS[currency]} onBack={() => setView('form')} onDone={onBack} t={t} />;
  }
  if (view === 'request-success') {
    return <RequestSuccessView requestId={requestId} amountNum={amountNum} currency={currency} method={method} onDone={onBack} />;
  }

  return (
    <motion.div variants={pageVariants} initial="initial" animate="animate" exit="exit" className="min-h-screen pb-10" style={{ background: SBER.bg }}>
      <SberHeader title={t ? t('topup.title') : 'Пополнение'} onBack={onBack} />
      <div className="px-5 pt-5">
        <div className="grid grid-cols-3 gap-2 mb-4" data-testid="topup-currency-selector">
          {TOPUP_CURRENCIES.map((curr) => (
            <motion.button
              key={curr}
              whileTap={tapAnimation}
              onClick={() => selectCurrency(curr)}
              className="py-3 rounded-xl font-semibold transition-colors flex items-center justify-center gap-1.5"
              style={{ background: currency === curr ? SBER.green : SBER.white, color: currency === curr ? 'white' : SBER.dark }}
              data-testid={`topup-currency-${curr}`}
            >
              <span>{TOPUP_CONFIG[curr].flag}</span>{curr}
            </motion.button>
          ))}
        </div>

        <SberCard className="p-5 mb-4">
          <label className="text-[13px] font-medium mb-3 block" style={{ color: SBER.green }}>{t ? t('topup.enterAmount') : 'Сумма пополнения'}</label>
          <div className="flex items-baseline gap-2">
            <input
              type="number"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="0"
              className="text-[36px] font-bold bg-transparent outline-none w-full"
              style={{ color: SBER.dark }}
              data-testid="amount-input"
            />
            <span className="text-[20px] font-semibold" style={{ color: SBER.green }}>{currency}</span>
          </div>
          <div className="flex flex-wrap gap-2 mt-5">
            {cfg.quick.map((amt) => (
              <motion.button
                key={amt}
                whileTap={tapAnimation}
                onClick={() => setAmount(String(amt))}
                className="px-4 py-2 rounded-xl text-[13px] font-semibold"
                style={{ background: amount === String(amt) ? SBER.green : SBER.greenPale, color: amount === String(amt) ? SBER.white : SBER.green }}
                data-testid={`quick-amount-${amt}`}
              >
                {amt.toLocaleString()}
              </motion.button>
            ))}
          </div>
        </SberCard>

        {needsMethod && (
          <SberCard className="p-4 mb-4" data-testid="topup-method-selector">
            <p className="text-[13px] font-medium mb-3" style={{ color: SBER.gray }}>Способ оплаты</p>
            <div className="flex gap-2">
              {cfg.methods.map((m) => (
                <motion.button
                  key={m}
                  whileTap={tapAnimation}
                  onClick={() => setMethod(m)}
                  className="flex-1 py-3 px-2 rounded-xl text-[13px] font-semibold"
                  style={{ background: method === m ? SBER.green : SBER.grayPale, color: method === m ? 'white' : SBER.dark }}
                  data-testid={`topup-method-${m}`}
                >
                  {METHOD_LABELS[m]?.ru || m}
                </motion.button>
              ))}
            </div>
          </SberCard>
        )}

        {amountNum > 0 && (
          <SberCard className="p-5 mb-4">
            <div className="space-y-3">
              <div className="flex justify-between text-[14px]">
                <span style={{ color: SBER.gray }}>{t ? t('withdraw.amount') : 'Сумма'}</span>
                <span className="font-medium" style={{ color: SBER.dark }}>{amountNum.toLocaleString()} {currency}</span>
              </div>
              <div className="flex justify-between text-[14px]">
                <span style={{ color: SBER.gray }}>{t ? t('topup.fee') : 'Комиссия'} {cfg.fee}%</span>
                <span className="font-medium" style={{ color: '#F59E0B' }}>−{fee.toFixed(2)} {currency}</span>
              </div>
              <div className="h-px" style={{ background: SBER.grayPale }} />
              <div className="flex justify-between items-center">
                <span className="font-semibold" style={{ color: SBER.dark }}>{t ? t('topup.toReceive') : 'К зачислению'}</span>
                <span className="text-[18px] font-bold" style={{ color: SBER.green }} data-testid="topup-net-amount">{netAmount.toFixed(2)} {currency}</span>
              </div>
            </div>
          </SberCard>
        )}

        {needsCaptcha && amountNum >= cfg.min && method && (
          <div className="mb-4">
            {captchaToken ? (
              <div className="flex items-center gap-2 p-4 rounded-2xl" style={{ background: SBER.greenPale }} data-testid="captcha-passed">
                <CheckCircle2 className="w-5 h-5" style={{ color: SBER.green }} />
                <p className="text-[14px] font-medium" style={{ color: SBER.dark }}>Проверка пройдена</p>
              </div>
            ) : (
              <CaptchaBox onVerified={setCaptchaToken} t={t} />
            )}
          </div>
        )}

        <motion.button
          whileTap={tapAnimation}
          onClick={handleTopup}
          disabled={loading || !canSubmit}
          className="w-full py-4 rounded-xl font-semibold text-[16px] text-white disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          style={{ background: SBER.green, boxShadow: '0 4px 16px rgba(33, 160, 56, 0.3)' }}
          data-testid="pay-button"
        >
          {loading ? <RefreshCw className="w-5 h-5 animate-spin" /> : cfg.mode === 'mia' ? <><QrCode className="w-5 h-5" />Оплатить через MIA QR</> : cfg.mode === 'bank' ? <><Building2 className="w-5 h-5" />{t ? t('topup.bankTransfer') : 'Получить реквизиты'}</> : <><Building2 className="w-5 h-5" />Создать заявку</>}
        </motion.button>

        <p className="text-center text-[13px] mt-4" style={{ color: SBER.grayLight }}>
          {t ? t('topup.min') : 'Минимум'} {cfg.min.toLocaleString()} {currency} • Зачисление 1–24 часа
        </p>
      </div>
    </motion.div>
  );
};

export default TopupPage;
